import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled.button`
  background: none;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  color: ${theme.colors.text};
  font-size: ${theme.typography.fontSizes.sm};
  font-weight: ${theme.typography.fontWeights.medium};
  font-family: ${theme.typography.fontFamily};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  min-width: 120px;
  justify-content: space-between;
  
  &:hover {
    background-color: ${theme.colors.surfaceHover};
  }
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const DropdownArrow = styled.span`
  font-size: ${theme.typography.fontSizes.xs};
  transition: transform ${theme.transitions.normal};
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-top: none;
  border-radius: 0 0 ${theme.borderRadius.sm} ${theme.borderRadius.sm};
  box-shadow: ${theme.shadows.md};
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  background: none;
  border: none;
  color: ${props => props.color || theme.colors.text};
  font-size: ${theme.typography.fontSizes.sm};
  font-weight: ${theme.typography.fontWeights.normal};
  font-family: ${theme.typography.fontFamily};
  text-align: left;
  cursor: pointer;
  
  &:hover {
    background-color: ${theme.colors.surfaceHover};
  }
  
  &:focus {
    outline: none;
    background-color: ${theme.colors.surfaceHover};
  }
`;

const Dropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select option",
  disabled = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const selectedOption = options.find(option => option.value === value);
  const displayValue = selectedOption ? selectedOption.label : placeholder;

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButton 
        onClick={handleToggle}
        disabled={disabled}
        type="button"
      >
        <span style={{ color: selectedOption?.color }}>{displayValue}</span>
        <DropdownArrow isOpen={isOpen}>▼</DropdownArrow>
      </DropdownButton>
      
      {isOpen && (
        <DropdownMenu>
          {options.map((option) => (
            <DropdownItem
              key={option.value}
              onClick={() => handleSelect(option.value)}
              type="button"
              color={option.color}
            >
              {option.label}
            </DropdownItem>
          ))}
        </DropdownMenu>
      )}
    </DropdownContainer>
  );
};

export default Dropdown; 